/**
 * Axios API Client with JWT Token Management
 *
 * Features:
 * - Automatic JWT token injection in request headers
 * - Automatic token refresh on 401 errors
 * - Request/response interceptors for error handling
 * - Configurable base URL and timeout
 */

import axios, { AxiosError } from "axios";
import type {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { toast } from "sonner";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  removeTokens,
} from "@/utils/token";

// Base API URL from environment variables
// Use relative URLs (empty string) so API calls use the same host as the frontend
// Only use localhost when explicitly running on localhost (for local development with separate frontend/backend servers)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

/**
 * Create Axios instance with default configuration
 * V230: Timeout 10초 → 30초로 증가 (DataStore 동기화 대기 시간 고려)
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Flag to prevent multiple simultaneous refresh token requests
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: string | null) => void;
  reject: (error?: Error | null) => void;
}> = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Request Interceptor: Auto-inject JWT token
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();

    // Skip token injection for auth endpoints
    if (
      config.url?.includes("/api/auth/login") ||
      config.url?.includes("/api/auth/refresh")
    ) {
      return config;
    }

    // Inject JWT token if available
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

/**
 * Response Interceptor: Handle token refresh on 401 errors
 * V201: SESSION_INVALIDATED 에러 처리 추가
 * P0-3: 403 에러 분기 처리 — RBAC 권한부족 vs 토큰오류
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError<{ error?: string; reason?: string; message?: string }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // V201: 세션 무효화 에러 처리 (다른 기기에서 로그인)
    if (error.response?.status === 401) {
      const errorData = error.response.data;
      if (errorData?.error === "SESSION_INVALIDATED") {
        // 세션이 무효화됨 - 토큰 제거 후 로그인 페이지로 리다이렉트
        removeTokens();
        toast.error(
          errorData.message ||
            "다른 기기에서 로그인하여 현재 세션이 종료되었습니다.",
          { duration: 4000 },
        );
        setTimeout(() => {
          window.location.href = "/auth";
        }, 1500);
        return Promise.reject(error);
      }
    }

    // Handle 401 Unauthorized errors (token expired)
    // 로그인 엔드포인트는 제외 — 로그인 실패 401은 호출자(handleLogin)에서 직접 처리
    const isAuthEndpoint =
      originalRequest.url?.includes("/api/auth/login") ||
      originalRequest.url?.includes("/api/auth/admin/login") ||
      originalRequest.url?.includes("/api/auth/register");
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        // No refresh token available, redirect to login
        isRefreshing = false;
        removeTokens();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        // Call refresh token API
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
          refreshToken,
        });

        const { accessToken } = response.data;
        setAccessToken(accessToken);

        // Update Authorization header for the original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Process all queued requests
        processQueue(null, accessToken);

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh token failed, redirect to login
        processQueue(refreshError as Error, null);
        removeTokens();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // P0-3 / V1173: 403 Forbidden — reason 필드로 동작 분기
    //   FORBIDDEN_ORG : 타 조직 데이터 접근 → 횟수 누적, 3회 이상 시 강제 로그아웃
    //   FORBIDDEN_ROLE : 권한 부족 → 토스트 + 대시보드 이동 (카운터 미포함)
    //   reason 없음(구버전) 또는 FORBIDDEN_TOKEN : 토큰 보안 위반 → 즉시 로그아웃
    if (error.response?.status === 403) {
      const reason = error.response.data?.reason;

      if (reason === "FORBIDDEN_ORG") {
        // sessionStorage 기반 횟수 누적 카운터
        const count =
          parseInt(sessionStorage.getItem("_fo_count") || "0") + 1;
        sessionStorage.setItem("_fo_count", String(count));

        if (count >= 3) {
          removeTokens();
          sessionStorage.removeItem("_fo_count");
          toast.error("비정상적인 접근이 감지되어 로그아웃되었습니다.", {
            duration: 4000,
          });
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
        } else {
          toast.warning("다른 조직의 데이터에 접근할 수 없습니다.", {
            duration: 3000,
          });
          setTimeout(() => {
            window.location.href = "/dashboard";
          }, 1500);
        }
        return Promise.reject(error);
      }

      if (reason === "FORBIDDEN_ROLE") {
        toast.warning("접근 권한이 없습니다.", { duration: 3000 });
        setTimeout(() => {
          if (window.history.length > 1) {
            window.history.back();
          } else {
            window.location.href = "/dashboard";
          }
        }, 1500);
        return Promise.reject(error);
      }

      // reason 없거나 FORBIDDEN_TOKEN: 토큰 보안 위반 → 즉시 로그아웃
      removeTokens();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // For other errors, reject the promise
    return Promise.reject(error);
  },
);

/**
 * Generic API error handler
 * 사용자 친화적 메시지 + 에러코드(ERR-xxx) 접미어로 디버깅 용이
 */
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const status = axiosError.response?.status;

    if (axiosError.response) {
      const backendMessage = axiosError.response.data?.message;
      const code = `ERR-${status}`;

      if (backendMessage) {
        // 백엔드가 구체적 메시지를 보낸 경우 그대로 사용 + 에러코드
        return `${backendMessage} (${code})`;
      }

      // 백엔드 메시지 없을 때 상태코드별 사용자 친화 메시지
      switch (status) {
        case 400:
          return `요청 내용을 확인해주세요. (${code})`;
        case 404:
          return `요청한 데이터를 찾을 수 없습니다. (${code})`;
        case 409:
          return `이미 처리된 요청입니다. 새로고침 후 다시 확인해주세요. (${code})`;
        case 422:
          return `입력 정보를 다시 확인해주세요. (${code})`;
        case 429:
          return `요청이 너무 많습니다. 잠시 후 다시 시도해주세요. (${code})`;
        case 500:
          return `서버에서 문제가 발생했습니다. 잠시 후 다시 시도해주세요. (${code})`;
        case 502:
        case 503:
          return `서버 점검 중이거나 일시적 오류입니다. 잠시 후 다시 시도해주세요. (${code})`;
        default:
          return `처리 중 문제가 발생했습니다. (${code})`;
      }
    } else if (axiosError.request) {
      return "서버와 연결할 수 없습니다. 네트워크를 확인해주세요. (ERR-NET)";
    } else {
      return "요청 처리 중 오류가 발생했습니다. (ERR-REQ)";
    }
  }

  return "알 수 없는 오류가 발생했습니다. (ERR-UNK)";
};

export default apiClient;
